<template>
  <div
    v-bc-uploader="bcUploaderSettings"
    class="UploadDropZoneParent"
    ref="uploaderEl"
  >
    <slot />
    <div
      v-if="visible"
      @click="$refs.uploaderEl.awVueUploader.fileInput.click()"
      class="UploadDropZone flex justify-center items-center border-1 border-dashed rounded-md cursor-pointer"
      :class="{ 'border-gray-400 hover:border-bc-blue bg-bc-gray-bg hover:text-bc-blue text-gray-400 px-6 pt-2': !publicUploadEnabled, 'bg-bc-blue text-white border-white hover:bg-bc-blue-light hover:text-bc-blue px-6 py-6': publicUploadEnabled }"
      ref="uploadDropZone"
    >
      <div
        v-if="uploadEnabled"
        class="flex flex-col justify-center items-center"
      >
        <div 
          class="space-x-1 text-center"
        >
          <svg
            class="inline-block h-6 w-6"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="relative cursor-pointer rounded-md">
            {{ $t('Drag the file here, or click to add a file') }}
          </span>
        </div>
        <template v-if="!publicUploadEnabled">
          <div class="UploadDropZone__Border border-b border-dashed mt-2 w-10/12"/>
          <div 
            class="UploadDropZone__Cloud space-x-1 text-center text-bc-blue text-xs cursor-pointer p-2"
            @click.prevent.stop="bcStorageExplorerStore.openModal(allFilesUploadDone, fileUploadDone, remoteFileUploadRequest, uploadStart)"
          >
            <aw-icon
              icon="storage"
            />
            <span class="">
              {{ $t('choose a file from data storage or cloud') }}
            </span>
          </div>
        </template>
      </div>
    </div>
    <div class="UploadDropZoneGhost" :class="{ 'bg-bc-gray-bg': !publicUploadEnabled, 'bg-bc-blue': publicUploadEnabled }" :style="uploadDropZoneGhostStyle"></div>
  </div>
</template>

<script>
import defaults from 'lodash/defaults'
import { $t } from 'nette-base-module/js/vuei18next'
// import cloneDeep from 'lodash/cloneDeep'
import { updateGlobalUploader } from '../../globalUploader'
import { mapStores } from 'pinia'
import useBcStorageExplorerStore from '@/frontapp/store/bcStorageExplorer'
import find from 'lodash/find'
import Toast from '@/frontapp/mixins/actions/Toast'
import { DocumentApi } from '../../test-api/index.ts'
import DocumentStore, { unfreezeDocumentStore, freezeActiveDocumentStore } from '@/frontapp/mixins/DocumentStore'
import UpdateDocumentRevision from '@/frontapp/mixins/actions/UpdateDocumentRevision'
import handleApiError from '../../utils/handleApiError'

const documentApi = new DocumentApi({}, '/api/v2')
const imageExts = [
  'png', 'jpg', 'gif', 'jpeg', 'psd', 'ai', 'tiff', 'tif', 'eps', 'eps2', 'eps3', 'epsf', 'epsi', 'epi', 'ico', 'svg', 'ps', 'ps2', 'ps3', 'bmp',
  'nef', /*'raw',*/ 'arw', 'x3f', /*'srw',*/ 'dng', 'pef', 'rw2', 'orf', 'mef', 'mrw', '3fr', 'mos', 'raf', 'erf', 'crw', 'cr2', 'tga', 
  'webp',
];

const getElementData = (file, parent__bc_document_data_rev_id, rank, isMounted) => {
  return {
    parent__bc_document_data_rev_id,
    type: 'file',
    data: {
      name: file.name,
      canva: false,
      can_download: true,
      public_upload: false,
      file_id: file.id
    },
    file,
    rank,
    updated: isMounted,
    key: Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substr(0, 5)
  }
}

const fileUploadDone = async (file, isMounted, {element, documentDropElement, publicUpload, isBCSharedDocument, bcDocumentStore}) => {

  let dropElementChildren, parent__bc_document_data_rev_id
  if (documentDropElement) {
    if (documentDropElement.id) {
      documentDropElement = bcDocumentStore.findElementInDocument(documentDropElement.id)
      dropElementChildren = documentDropElement.children
      parent__bc_document_data_rev_id = documentDropElement.id

    } else { // document drop element can be not saved yet
      dropElementChildren = documentDropElement.children
      parent__bc_document_data_rev_id = null
    }

  } else {
    element = bcDocumentStore.findElementInDocument(element.id)
    dropElementChildren = element.children
    parent__bc_document_data_rev_id = element.id
  }

  const existingChildren = find(dropElementChildren, { file: { id: file.id } })
  if (existingChildren) {
    Toast(
      'error',
      $t('File already exist in list'),
      $t(
        'File "%fileName%" already exist in this list of files under the name "%fileListName%"',
        {
          fileName: file.name,
          fileListName: existingChildren.file.name
        }
      )
    )
    return
  }

  if (publicUpload) {
    let addFileResponse
    if (isBCSharedDocument) {
      addFileResponse = await handleApiError(() => documentApi.elementPublicUploadShared(
        bcDocumentStore.document.id,
        element.id,
        { file_id: file.id }
      ))
    } else {
      addFileResponse = await handleApiError(() => documentApi.elementPublicUpload(
        bcDocumentStore.document.id,
        element.id,
        { file_id: file.id }
      ))
    }

    const fileResponseElement = {
      ...addFileResponse.data,
      key: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, '')
        .substr(0, 5)
    }

    dropElementChildren.push(fileResponseElement)
    return fileResponseElement
  }

  const constructedElement = getElementData(
    file, 
    parent__bc_document_data_rev_id,
    dropElementChildren.length,
    isMounted,
  )

  dropElementChildren.push(constructedElement)
  return constructedElement
}

const fileUploadDoneUnmountedCallback = (frozenContext) => {
  return async function(file) {
    const element = await fileUploadDone(file, false, frozenContext)
    console.log('fileUploadDoneUnmountedCallback file done', element, frozenContext)

    if (!element) {
      return
    }

    const metaData = {}

    console.log('fileUploadDoneUnmountedCallback add saveElement to document update queue', element, frozenContext)
    await frozenContext.bcDocumentStore.saveElement(
      element,
      metaData,
      async (savedElement, resolve) => {
        // this.mergeElementData(this.element.data, savedElement.data)
        if (element.id === undefined) {
          delete savedElement.key
          defaults(element, savedElement)
          element.parent__bc_document_data_rev_id = savedElement.parent__bc_document_data_rev_id // may change over revision
        }

        frozenContext.bcDocumentStore.addToFlatten(element)

        if (savedElement.bc_document_rev_id !== frozenContext.bcDocumentStore.documentRev) {
          // have to reload document
          console.log('will load document in new revision', frozenContext.bcDocumentStore.document.id, savedElement.bc_document_rev_id)
          await UpdateDocumentRevision(frozenContext.bcDocumentStore, savedElement.bc_document_rev_id, null, false)
          frozenContext.bcDocumentStore.loadDocumentRevisions(frozenContext.bcDocumentStore.document.id)
        }

        console.log('resolve promise with updated element')
        resolve()
      }
    )
  }
}

const allFilesUploadDone = (files, uploaderId, {canChangeViewType, publicUpload, documentDropElement, element, bcDocumentStore}) => {
  if (canChangeViewType && publicUpload === false) {
    if (files.every((file) => imageExts.includes(file.ext)) && files.length > 1) {
      if (documentDropElement) {
        documentDropElement.type = 'gallery'
        documentDropElement.updated = 'type'
      } else {
        element.type = 'gallery'
        element.updated = 'type'
      }
    }
  }

  bcDocumentStore.uploadersRunning.delete(uploaderId)
  if (bcDocumentStore.uploadersRunning.size === 0) {
    unfreezeDocumentStore(bcDocumentStore.document.id, bcDocumentStore.documentRev)
  }
}

const allFilesUploadDoneUnmountedCallback = (frozenContext) => {
  return async function(files, uploaderId) {
    allFilesUploadDone(files, uploaderId, frozenContext)
    console.log('allFilesUploadDoneUnmountedCallback file done', frozenContext)

    if (frozenContext.bcDocumentStore.uploadersRunning.size === 0) {
      if (typeof frozenContext.bcDocumentStore.uploaderCallback === 'function') {
        await frozenContext.bcDocumentStore.uploaderCallback()
      }
    }
  }
}

const uploadStopCallback = ({ bcDocumentStore }) => {
  return async function (uploaderId) {
    bcDocumentStore.uploadersRunning.delete(uploaderId)

    // this was last running uploader
    if (bcDocumentStore.uploadersRunning.size === 0) {
      unfreezeDocumentStore(bcDocumentStore.document.id, bcDocumentStore.documentRev)
      if (typeof bcDocumentStore.uploaderCallback === 'function') { // document was left because bcDocumentStore.uploaderCallback is function
        bcDocumentStore.uploaderCallback = null
        await bcDocumentStore.willLeave()
        bcDocumentStore.reset()
      }
    }
  }
}

export default {
  mixins: [
    DocumentStore,
  ],
  emits: ['update:modelValue', 'update:modelChildren'],
  props: {
    modelValue: Object,
    modelChildren: Array,
    uploadEnabled: Boolean,
    publicUpload: Boolean,
    visible: Boolean,
  },
  data() {
    return {
      documentDropElement: null,
      canChangeViewType: false,
      uploadDropZoneGhostStyle: null,
      globalUploaderId: null,
      bcUploaderSettings: {
        name: 'DocumentUploadDropZone',
        onUploadStart: null,
        onFileUploadDone: null,
        onFileUploadDoneUnmounted: null,
        onAllFilesUploadDone: null,
        onAllFilesUploadDoneUnmounted: null,
        onUploadStop: null,
        onUploadStopUnmounted: null,
        click: false,
        highlightTimeout: 50,
      }
    }
  },
  computed: {
    ...mapStores(useBcStorageExplorerStore),
    element: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    children: {
      get() {
        return this.modelChildren
      },
      set(val) {
        this.$emit('update:modelChildren', val)
      }
    },
    publicUploadEnabled() {
      return this.element.data?.public_upload
    },
    isUploaderActive() {
      return this.visible && this.uploadEnabled
    }
  },
  watch: {
    visible: {
      handler(val) {
        if (!val) {
          return
        }

        this.$nextTick(() => {
          const rect = this.$refs.uploadDropZone.getBoundingClientRect()
          this.uploadDropZoneGhostStyle = {
            width: rect.width + 'px',
            height: rect.height + 'px',
          }
        })
      },
      flush: 'post',
    },
    isUploaderActive: {
      handler(val) {
        this.bcUploaderSettings = this.createBcUploaderSettings(val)
      },
      immediate: true
    },
    canChangeViewType() {
      if (this.isUploaderActive) {
        this.bcUploaderSettings = this.createBcUploaderSettings(true)
      }
    },
    documentDropElement() {
      if (this.isUploaderActive) {
        this.bcUploaderSettings = this.createBcUploaderSettings(true)
      }
    }
  },
  methods: {
    createBcUploaderSettings(active) {
      const frozenContext = this.freezeContext()
      console.log('updating uploader settings from uploader dropzone', frozenContext.bcDocumentStore)

      return {
        name: 'DocumentUploadDropZone',
        onUploadStart: active ? this.uploadStart : null,
        onFileUploadDone: active ? this.fileUploadDone : null,
        onFileUploadDoneUnmounted: active ? () => fileUploadDoneUnmountedCallback(frozenContext) : null,
        onAllFilesUploadDone: active ? this.allFilesUploadDone : null,
        onAllFilesUploadDoneUnmounted: active ? () => allFilesUploadDoneUnmountedCallback(frozenContext) : null,
        onUploadStop: active ? this.uploadStop : null,
        onUploadStopUnmounted: active ? () => uploadStopCallback(frozenContext) : null,
        click: false,
        highlightTimeout: 50,
      }
    },
    freezeContext() {
      return {
        element: this.element, 
        documentDropElement: this.documentDropElement, 
        publicUpload: this.publicUpload, 
        isBCSharedDocument: this.$route.matched[0].name === 'BCSharedDocument',
        canChangeViewType: this.canChangeViewType, 
        bcDocumentStore: this.bcDocumentStore
      }
    },
    createOrGetDropElementChildren() {
      if (
        ['files', 'gallery', 'image', 'preview'].includes(this.element.type)
      ) {
        return this.children
      
      } else if (this.documentDropElement) {
        return this.documentDropElement.children
      }

      const page = this.children[this.children.length - 1]

      if (
        page.children.length > 0 && ['files', 'gallery', 'image', 'preview'].includes(page.children[page.children.length - 1].type)
      ) {
        this.documentDropElement = page.children[page.children.length - 1]
      
      } else {
        this.documentDropElement = {
          parent__bc_document_data_rev_id: page.id,
          type: 'preview',
          data: {
            canva: false,
            can_download: true,
            public_upload: false,
            sort: 'rank',
            sort_dir: 'asc'
          },
          rank: page.children.length,
          updated: true,
          key: Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, '')
            .substr(0, 5),
          children: []
        }

        page.children.push(this.documentDropElement)
      }

      return this.documentDropElement.children
    },
    uploadStop(uploaderId) {
      (uploadStopCallback(this.freezeContext()))(uploaderId)
    },
    uploadStart(uploaderId) {
      console.log('upload start', uploaderId)
      this.bcDocumentStore.uploadersRunning.add(uploaderId)
      freezeActiveDocumentStore()

      const dropElementChildren = this.createOrGetDropElementChildren()
      if (dropElementChildren?.length === 0) {
        this.canChangeViewType = true
      }

      if (this.element.type === 'document' && uploaderId !== 'storage-explorer') {
        const frozenContext = this.freezeContext()
        console.log('update global uploader with frozen context', uploaderId, frozenContext)
        updateGlobalUploader({
          uploaderId: this.globalUploaderId,
          onUploadStart: this.uploadStart,
          onFileUploadDone: this.fileUploadDone,
          onFileUploadDoneUnmounted: () => fileUploadDoneUnmountedCallback(frozenContext),
          onAllFilesUploadDone: this.allFilesUploadDone,
          onAllFilesUploadDoneUnmounted: () => allFilesUploadDoneUnmountedCallback(frozenContext),
          onUploadStop: this.uploadStop,
          onUploadStopUnmounted: () => uploadStopCallback(frozenContext),
          enabled: true,
        })
      }
    },
    async fileUploadDone(file) {
      await fileUploadDone(file, true, this.freezeContext())
    },
    async remoteFileUploadRequest(fileRequest) {
      const children = this.createOrGetDropElementChildren()
      
      if (children.length === 0) {
        this.canChangeViewType = true
      }

      const existingChildren = find(children, { file_upload: { id: fileRequest.id } })
      if (existingChildren) {
        Toast(
          'error',
          this.$t('File already exist in list'),
          this.$t(
            'File "%fileName%" already exist in this list of files under the name "%fileListName%"',
            {
              fileName: fileRequest.name,
              fileListName: existingChildren.file_upload.name
            }
          )
        )
        return
      }

      children.push({
        parent__bc_document_data_rev_id:
          this.element.type !== 'document'
            ? this.element.id
            : this.documentDropElement.id,
        type: 'file',
        data: {
          name: fileRequest.name,
          canva: false,
          can_download: true,
          public_upload: false,
          file_id: fileRequest.file_id ? fileRequest.file_id : null,
          bc_file_upload_id: fileRequest.id,
        },
        file_upload: fileRequest,
        file: null,
        rank: children.length,
        updated: true,
        key: Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')
          .substr(0, 5)
      })
    },
    allFilesUploadDone(files, uploaderId) {
      allFilesUploadDone(files, uploaderId, this.freezeContext())

      this.documentDropElement = null
      this.canChangeViewType = false
    },
  },
  mounted() {
    if (this.element.type !== 'document') {
      return
    }

    this.globalUploaderId = this.bcDocumentStore.currentBcDocumentStoreId

    // global uploader for document is already running, so init it thru upload start method
    if (this.bcDocumentStore.uploadersRunning.has(this.globalUploaderId)) {
      console.log('update global uploader with uploadStart', this.globalUploaderId)
      this.uploadStart(this.globalUploaderId)
      return
    }

    console.log('update global uploader', this.globalUploaderId)
    updateGlobalUploader({
      uploaderId: this.globalUploaderId,
      onUploadStart: this.uploadStart,
      onFileUploadDone: this.fileUploadDone,
      onFileUploadDoneUnmounted: null,
      onAllFilesUploadDone: this.allFilesUploadDone,
      onAllFilesUploadDoneUnmounted: null,
      onUploadStop: this.uploadStop,
      enabled: true,
    })
  },
  beforeUnmount() {
    if (this.element.type !== 'document') {
      return
    }

    updateGlobalUploader({
      enabled: false,
    })
  }
}
</script>
<style lang="scss" scoped>
.UploadDropZoneParent.upload-hightlight {
  @apply relative;

  .UploadDropZone {
    @apply absolute inset-0 z-10 text-bc-blue border-bc-blue bg-white opacity-90;
  }

  .UploadDropZoneGhost {
    display: block;
  }
}

.UploadDropZoneParent {
  .UploadDropZoneGhost {
    display: none;
  }
}

.UploadDropZone {
  min-height: 2.75rem;

  .UploadDropZone__Border {
    @apply border-gray-400;
  }
}

.UploadDropZone:hover {
  .UploadDropZone__Border {
    @apply border-bc-blue;
  }
}

.UploadDropZone:hover:has(.UploadDropZone__Cloud:hover) {
  @apply border-gray-400 text-gray-400;

  .UploadDropZone__Border {
    @apply border-gray-400;
  }
}

// dropzona na konci bude vyssi
.UploadDropZoneParent.Content__Document .UploadDropZone > * {
  @apply py-6;
  // @apply w-full;
}
</style>
